/**
 * VRG-735 (Spencer, 2026-10-22) — the SOCIAL NETWORK under the Social source channel.
 *
 * `social_display_ads` (#5, "Social (organic)") stays the SOURCE: every social referrer and every
 * `utm_medium=social` link is still that one channel, so the 19-channel map, the lead-source
 * groups and VRG-696's "one stored value per channel" rule are unchanged. What is new is the
 * SUB-SOURCE beneath it, so GTM → Channels can answer "does LinkedIn or Instagram bring visits
 * and deals":
 *
 *   linkedin       linkedin.com (any subdomain), lnkd.in; utm_source linkedin / li / lnkd
 *   instagram      instagram.com (any subdomain, e.g. l.instagram.com); utm_source instagram / ig
 *   other_social   every other social site Vera already treats as Social: facebook.com, fb.com,
 *                  t.co, x.com, twitter.com, tiktok.com, pinterest.*, youtube.com, threads.net,
 *                  reddit.com; and their utm_source spellings
 *
 * Anything else is NULL here (not guessed). The lead-source resolver then files a lead that IS
 * Social (by its referrer or its utm_medium) but names no network it knows under `other_social`:
 * it is demonstrably social, just not LinkedIn or Instagram. A lead set to Social BY HAND with no
 * signal gets no network at all ("not split" on Channels).
 *
 * Stored as a typed sub-source (`@repo/types` gtm/sub-source.ts, kind `social_network`, id the
 * network), so a person can also pick it, and deals group by it like any other sub-source.
 */

export const SOCIAL_NETWORKS = ['linkedin', 'instagram', 'other_social'] as const;
export type SocialNetwork = (typeof SOCIAL_NETWORKS)[number];
export const isSocialNetwork = (v: unknown): v is SocialNetwork => typeof v === 'string' && (SOCIAL_NETWORKS as readonly string[]).includes(v);

export const SOCIAL_NETWORK_LABELS: Readonly<Record<SocialNetwork, string>> = {
  linkedin: 'LinkedIn',
  instagram: 'Instagram',
  other_social: 'Other social',
};

/**
 * The social hosts, one list for both questions: "is this referrer Social?" (`referrerChannel`)
 * and "which network?" (`socialNetworkOfHost`). Matched against a lowercased host with `www.`
 * removed, on a label boundary, so `l.instagram.com` is Instagram and `notinstagram.com` is not.
 */
export const SOCIAL_NETWORK_HOSTS: Readonly<Record<SocialNetwork, RegExp>> = {
  linkedin: /(^|\.)(linkedin\.com|lnkd\.in)$/,
  instagram: /(^|\.)instagram\.com$/,
  other_social: /(^|\.)(facebook\.com|fb\.com|t\.co|x\.com|twitter\.com|tiktok\.com|pinterest\.[a-z.]+|youtube\.com|threads\.net|reddit\.com)$/,
};

/** utm_source spellings per network (lowercased, trimmed; `_`/space read as `-`). */
const SOCIAL_NETWORK_UTM_SOURCES: Readonly<Record<SocialNetwork, readonly string[]>> = {
  linkedin: ['linkedin', 'li', 'lnkd', 'linkedin-com'],
  instagram: ['instagram', 'ig', 'instagram-com'],
  other_social: ['facebook', 'fb', 'meta', 'x', 'twitter', 'tiktok', 'pinterest', 'youtube', 'threads', 'reddit'],
};

const normHost = (host: string | null | undefined): string => (host ?? '').trim().toLowerCase().replace(/^www\./, '');

/** A referrer host → its network, or null when it is not a social host Vera knows. */
export const socialNetworkOfHost = (host: string | null | undefined): SocialNetwork | null => {
  const h = normHost(host);
  if (!h) return null;
  for (const n of SOCIAL_NETWORKS) if (SOCIAL_NETWORK_HOSTS[n].test(h)) return n;
  return null;
};

/** A utm_source → its network, or null when it names none Vera knows. */
export const socialNetworkOfUtmSource = (source: string | null | undefined): SocialNetwork | null => {
  const s = (source ?? '').trim().toLowerCase().replace(/[\s_]+/g, '-');
  if (!s) return null;
  for (const n of SOCIAL_NETWORKS) if (SOCIAL_NETWORK_UTM_SOURCES[n].includes(s)) return n;
  return null;
};

/** Is this host one of the social hosts (any network)? */
export const isSocialHost = (host: string | null | undefined): boolean => socialNetworkOfHost(host) !== null;

/**
 * A touch → its network. The utm_source wins when it names one (a tagged link says where it was
 * posted, even when the browser sent no referrer); otherwise the referrer host. Null = neither
 * names a network Vera knows.
 */
export const socialNetworkOf = (t: {host?: string | null; utmSource?: string | null}): SocialNetwork | null =>
  socialNetworkOfUtmSource(t.utmSource) ?? socialNetworkOfHost(t.host);
